import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Compass, Sparkles, RefreshCw, AlarmClock } from "lucide-react";
import { toast } from "sonner";
import TraitRadar from "@/components/TraitRadar";

const TRAIT_LABEL = {
  openness: "Openness", conscientiousness: "Consc.", extraversion: "Extraversion",
  agreeableness: "Agreeable", emotional_stability: "Stability",
};

export default function HiringCompassCard() {
  const [compass, setCompass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizOpen, setQuizOpen] = useState(false);
  const [qs, setQs] = useState([]);
  const [likert, setLikert] = useState([]);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/recruit/hiring-compass").then(r => setCompass(r.data)).catch(() => setCompass(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // 6-month retake reminder: compute freshness once compass is loaded.
  const retake = useMemo(() => {
    if (!compass?.updated_at) return null;
    const then = new Date(compass.updated_at).getTime();
    if (isNaN(then)) return null;
    const days = Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
    const isDue = days >= 180;
    const isSoon = !isDue && days >= 150; // last month of freshness
    // Respect a per-user dismissal for 14 days (per compass version)
    const dismissedRaw = localStorage.getItem("compass_retake_dismissed_until");
    const dismissedUntil = dismissedRaw ? new Date(dismissedRaw).getTime() : 0;
    const dismissed = Date.now() < dismissedUntil;
    return { days, isDue, isSoon, dismissed };
  }, [compass]);

  const dismissRetake = () => {
    const until = new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString();
    localStorage.setItem("compass_retake_dismissed_until", until);
    // Force re-render by nudging state
    setCompass({ ...compass });
    toast.success("We'll remind you again in 2 weeks");
  };

  const openQuiz = async () => {
    try {
      const { data } = await api.get("/recruit/hiring-compass/questions");
      setQs(data.questions); setLikert(data.likert);
      // Prefill with previous answers... we only stored scores, not answers, so start fresh
      setAnswers({});
      setQuizOpen(true);
    } catch { toast.error("Couldn't load quiz"); }
  };

  const submit = async () => {
    if (Object.keys(answers).length < qs.length) { toast.error(`Answer all ${qs.length} questions`); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/recruit/hiring-compass", { answers });
      setCompass(data);
      setQuizOpen(false);
      localStorage.removeItem("compass_retake_dismissed_until");
      toast.success("Hiring compass saved — candidate diversity comparisons enabled");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="hiring-compass-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-pink" />
            <h3 className="font-semibold">Hiring compass</h3>
            {compass && <Badge className="bg-teal text-white border-0 text-[10px]">Active</Badge>}
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={openQuiz} data-testid="compass-take-btn">
            {compass ? (<><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retake</>) : (<><Sparkles className="w-3.5 h-3.5 mr-1" /> Take the quiz</>)}
          </Button>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Save your own Big Five profile so Alfred can flag candidates who bring diverse traits to your team.
          Diverse hiring reviewers surface stronger candidates.
        </p>

        {retake && !retake.dismissed && (retake.isDue || retake.isSoon) && (
          <div
            data-testid="compass-retake-nudge"
            className={`rounded-lg border-l-4 p-3 mb-4 flex items-start gap-3 ${retake.isDue ? "border-l-pink bg-pink/5" : "border-l-teal bg-teal/5"}`}>
            <AlarmClock className={`w-4 h-4 mt-0.5 shrink-0 ${retake.isDue ? "text-pink" : "text-teal"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">
                {retake.isDue
                  ? "Your compass is 6+ months old — time for a refresh"
                  : `Compass check-in coming up in ${180 - retake.days} days`}
              </div>
              <div className="text-xs text-neutral-600 mt-0.5">
                {retake.isDue
                  ? `Last taken ${retake.days} days ago. People grow — retake the 10-question quiz so "diverse from you" stays accurate.`
                  : `You last took it ${retake.days} days ago. A retake keeps candidate comparisons sharp.`}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Button size="sm" className="btn-primary rounded-full text-xs h-7" onClick={openQuiz} data-testid="compass-retake-now-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Retake now
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-7 text-neutral-500" onClick={dismissRetake} data-testid="compass-retake-dismiss-btn">
                  Remind me later
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading && <div className="text-sm text-neutral-500">Loading…</div>}

        {!loading && !compass && (
          <div className="text-neutral-500 text-sm py-2">
            Take the 10-question quiz once — it takes about 3 minutes and unlocks "Diverse from you" flags on candidates.
          </div>
        )}

        {compass?.scores && (
          <div className="grid sm:grid-cols-2 gap-4 items-center" data-testid="compass-result">
            <TraitRadar candidate={compass.scores} team={null} size={220} />
            <div>
              <div className="space-y-1.5">
                {Object.entries(compass.scores).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3 text-xs">
                    <div className="w-24 shrink-0 text-neutral-700">{TRAIT_LABEL[k] || k}</div>
                    <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                      <div className="h-full bg-pink" style={{ width: `${v}%` }} />
                    </div>
                    <div className="w-8 text-right font-mono font-semibold">{v}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-neutral-500 mt-3" data-testid="compass-freshness">
                Saved {compass.updated_at ? new Date(compass.updated_at).toLocaleDateString() : "—"}
                {retake?.days != null && <> · <span className={retake.isDue ? "text-pink font-semibold" : ""}>{retake.days === 0 ? "today" : `${retake.days} day${retake.days === 1 ? "" : "s"} ago`}</span></>}
                {" "}· Overall {compass.overall_score}/100
              </div>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={quizOpen} onOpenChange={setQuizOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="compass-quiz-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Compass className="w-4 h-4 text-pink" /> Your hiring compass</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              10 short statements about you. Pick what feels most true — there are no right answers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {qs.map((q, i) => (
              <div key={q.id} className="border-b last:border-b-0 pb-3 last:pb-0" data-testid={`compass-q-${q.id}`}>
                <div className="text-sm mb-2">
                  <span className="text-pink font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>{q.label}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {likert.map(opt => {
                    const selected = answers[q.id] === opt.value;
                    return (
                      <button key={opt.value} onClick={() => setAnswers({ ...answers, [q.id]: opt.value })}
                        data-testid={`compass-${q.id}-${opt.value}`}
                        className={`flex-1 min-w-[90px] px-2 py-1.5 rounded-lg border text-xs transition-all ${selected ? "bg-pink text-white border-pink font-semibold" : "hover:bg-neutral-50"}`}>
                        <div className="font-mono text-[10px] opacity-70">{opt.value}</div>
                        <div>{opt.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuizOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={submit} disabled={saving} data-testid="compass-save-btn">
              {saving ? "Saving…" : "Save my compass"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
